import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { TrendingUp, TrendingDown, Scale, Target, Calendar } from 'lucide-react';
import { DatabaseSchema } from '../types';
import { format, subDays, differenceInDays, eachWeekOfInterval, eachMonthOfInterval, isAfter } from 'date-fns';

interface ProgressTrackerProps {
  database: DatabaseSchema | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

type ChartView = 'weight' | 'bmi' | 'both';

export const ProgressTracker: React.FC<ProgressTrackerProps> = ({ database }) => {
  const [tabValue, setTabValue] = useState(0);
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter' | 'year' | 'all' | 'goal'>('goal');
  const [chartView, setChartView] = useState<ChartView>('both');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Calculate BMI for each weight entry
  const calculateBMI = (weight: number) => {
    const heightInMeters = ((database?.preferences?.height || 169) / 100);
    return weight / (heightInMeters * heightInMeters);
  };

  // Generate full timeline data from start to goal date
  const getFullTimelineData = useMemo(() => {
    // Use start date from preferences, fallback to goals
    const startDateStr = database?.preferences?.startDate || database?.goals?.startDate;
    if (!startDateStr || !database?.goals) return [];

    const goals = database.goals;
    const weights = database.weights || [];
    
    const startDate = new Date(startDateStr);
    const today = new Date();
    
    // Calculate the expected weight loss trend based on monthly target
    const monthlyTarget = database?.preferences?.monthlyWeightLossTarget || 4.0;
    
    // Calculate proper target date based on current preferences
    const initialWeight = database?.preferences?.initialWeight || goals.initialWeight;
    const goalWeight = database?.preferences?.goalWeight || goals.targetWeight;
    const totalWeightToLose = initialWeight - goalWeight;
    if (monthlyTarget <= 0 || totalWeightToLose <= 0) return [];
    const monthsNeeded = totalWeightToLose / monthlyTarget;
    const calculatedTargetDate = new Date(startDate);
    calculatedTargetDate.setMonth(calculatedTargetDate.getMonth() + Math.ceil(monthsNeeded));
    
    // Use calculated target date instead of potentially outdated goals.targetDate
    const targetDate = calculatedTargetDate;
    const dailyWeightLoss = monthlyTarget / 30.44; // Average days per month

    // Create a set of all unique dates (both actual weights and intervals for projection)
    const allDates = new Set<string>();
    
    // Add all actual weight entry dates
    weights.forEach(w => {
      allDates.add(w.date);
    });
    
    // Add interval dates for the projection line
    const weeks = eachWeekOfInterval({
      start: startDate,
      end: targetDate
    });
    
    // If timeline is more than 52 weeks, use monthly intervals for projection
    const useMonthly = weeks.length > 52;
    const intervals = useMonthly
      ? eachMonthOfInterval({ start: startDate, end: targetDate })
      : weeks;
      
    intervals.forEach(date => {
      allDates.add(format(date, 'yyyy-MM-dd'));
    });
    
    // Convert to sorted array of dates
    const sortedDates = Array.from(allDates).sort();
    
    // Create a map of actual weight entries
    const weightMap = new Map();
    weights.forEach(w => {
      weightMap.set(w.date, w);
    });

    // Generate data points for all dates
    const data = sortedDates.map(dateStr => {
      const date = new Date(dateStr);
      const displayDate = format(date, 'MMM yyyy'); // Simplified format for better display
      
      // Days from start
      const daysFromStart = Math.max(0, differenceInDays(date, startDate));
      
      // Calculate expected weight for this date based on monthly target
      // Use goal weight from preferences as the minimum (final target)
      const finalGoalWeight = database?.preferences?.goalWeight || goals.targetWeight;
      const startingWeight = database?.preferences?.initialWeight || goals.initialWeight;
      const expectedWeight = Math.max(
        finalGoalWeight,
        startingWeight - (dailyWeightLoss * daysFromStart)
      );
      
      // Get actual weight if it exists for this date
      const weightEntry = weightMap.get(dateStr);
      const actualWeight = weightEntry ? weightEntry.weight : null;
      const actualBMI = actualWeight ? calculateBMI(actualWeight) : null;
      
      // Check if this is a future date
      const isFuture = isAfter(date, today);
      
      return {
        date: displayDate,
        fullDate: dateStr,
        expectedWeight: expectedWeight,
        expectedBMI: calculateBMI(expectedWeight),
        actualWeight: actualWeight,
        actualBMI: actualBMI,
        isFuture: isFuture,
        isToday: format(date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')
      };
    });

    return data;
  }, [database]);

  if (!database) {
    return (
      <Box p={3}>
        <Typography>Loading progress data...</Typography>
      </Box>
    );
  }

  const { weights, goals, bodyMeasurements, exercises, supplements, meals, preferences } = database;

  // Weight Progress Data for other time ranges
  const getWeightData = () => {
    const cutoffDate = timeRange === 'all' ? new Date('2020-01-01') :
                      timeRange === 'year' ? subDays(new Date(), 365) :
                      timeRange === 'quarter' ? subDays(new Date(), 90) :
                      timeRange === 'month' ? subDays(new Date(), 30) :
                      timeRange === 'week' ? subDays(new Date(), 7) :
                      null;

    if (timeRange === 'goal') {
      return getFullTimelineData;
    }

    if (!cutoffDate) return [];

    return weights
      .filter(w => new Date(w.date) >= cutoffDate)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map(w => ({
        date: format(new Date(w.date), 'MMM dd'),
        actualWeight: w.weight,
        actualBMI: calculateBMI(w.weight)
      }));
  };

  // Body Measurements Data
  const getMeasurementsData = () => {
    const cutoffDate = timeRange === 'all' ? new Date('2020-01-01') :
                      timeRange === 'year' ? subDays(new Date(), 365) :
                      timeRange === 'quarter' ? subDays(new Date(), 90) :
                      timeRange === 'month' ? subDays(new Date(), 30) :
                      subDays(new Date(), 7);

    return bodyMeasurements
      .filter(m => new Date(m.date) >= cutoffDate)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map(m => ({
        date: format(new Date(m.date), 'MMM dd'),
        ...m.measurements
      }));
  };

  // Activity Data
  const getActivityData = () => {
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = subDays(new Date(), 29 - i);
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayStr = format(date, 'MMM dd');
      
      const dayExercises = exercises.filter((e: any) => e.date === dateStr);
      const dayMeals = meals.filter((m: any) => m.date === dateStr);
      const daySupplements = supplements.find((s: any) => s.date === dateStr);
      
      return {
        date: dayStr,
        exercises: dayExercises.reduce((sum: number, e: any) => sum + e.duration, 0),
        meals: dayMeals.filter((m: any) => m.completed).length,
        supplements: daySupplements ? 
          Object.values(daySupplements.supplements).filter(Boolean).length : 0
      };
    });

    return last30Days;
  };

  // Progress Statistics
  const getProgressStats = () => {
    if (!goals || weights.length === 0) return null;

    const sortedW = [...weights].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const currentWeight = sortedW[sortedW.length - 1]?.weight || 0;
    const initialWeight = database?.preferences?.initialWeight || goals.initialWeight;
    const goalWeight = database?.preferences?.goalWeight || goals.targetWeight;
    const weightLost = initialWeight - currentWeight;
    const totalToLose = initialWeight - goalWeight;
    const progressPercentage = totalToLose > 0 ? (weightLost / totalToLose) * 100 : 0;

    const startDateForCalc = database?.preferences?.startDate || goals.startDate;
    const daysElapsed = differenceInDays(new Date(), new Date(startDateForCalc));

    const monthlyTarget = database?.preferences?.monthlyWeightLossTarget || 4.0;
    const monthsNeeded = monthlyTarget > 0 && totalToLose > 0 ? totalToLose / monthlyTarget : 0;
    const calculatedTargetDate = new Date(startDateForCalc);
    calculatedTargetDate.setMonth(calculatedTargetDate.getMonth() + Math.ceil(monthsNeeded || 1));

    const totalDays = differenceInDays(calculatedTargetDate, new Date(startDateForCalc));
    const timeProgressPercentage = totalDays > 0 ? (daysElapsed / totalDays) * 100 : 0;

    const expectedWeightLoss = (daysElapsed / 7) * (goals.weeklyTarget || 1);
    const actualVsExpected = weightLost - expectedWeightLoss;

    return {
      weightLost: weightLost.toFixed(1),
      totalToLose: totalToLose.toFixed(1),
      progressPercentage: Math.max(0, progressPercentage).toFixed(1),
      timeProgressPercentage: Math.min(100, timeProgressPercentage).toFixed(1),
      actualVsExpected: actualVsExpected.toFixed(1),
      remainingWeight: (currentWeight - goalWeight).toFixed(1),
      daysRemaining: Math.max(0, differenceInDays(calculatedTargetDate, new Date())),
      currentBMI: calculateBMI(currentWeight).toFixed(1),
      targetBMI: calculateBMI(goalWeight).toFixed(1)
    };
  };

  const weightData = getWeightData();
  const measurementsData = getMeasurementsData();
  const activityData = getActivityData();
  const progressStats = getProgressStats();

  const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#0ea5e9', '#f59e0b'];

  // Custom tooltip for the weight chart
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            p: 1.5
          }}
        >
          <Typography variant="body2" fontWeight="bold" color="#0f172a">{label}</Typography>
          {data.actualWeight && (
            <>
              <Typography variant="body2" color="primary">
                Weight: {data.actualWeight.toFixed(1)} kg
              </Typography>
              {chartView !== 'weight' && (
                <Typography variant="body2" color="warning.main">
                  BMI: {data.actualBMI?.toFixed(1)}
                </Typography>
              )}
            </>
          )}
          {data.expectedWeight && !data.actualWeight && (
            <>
              <Typography variant="body2" color="text.secondary">
                Estimated: {data.expectedWeight.toFixed(1)} kg
              </Typography>
              {chartView !== 'weight' && (
                <Typography variant="body2" color="text.secondary">
                  Estimated BMI: {data.expectedBMI?.toFixed(1)}
                </Typography>
              )}
            </>
          )}
          {data.isToday && (
            <Chip label="Today" size="small" color="primary" sx={{ mt: 0.5 }} />
          )}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box p={{ xs: 2, sm: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
          Progress Tracking
        </Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            label="Time Range"
          >
            <MenuItem value="week">Last Week</MenuItem>
            <MenuItem value="month">Last Month</MenuItem>
            <MenuItem value="quarter">Last 3 Months</MenuItem>
            <MenuItem value="year">Last Year</MenuItem>
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="goal">Full Timeline</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {progressStats && (
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Scale size={24} color="#6366f1" />
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {progressStats.weightLost} kg
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Weight Lost
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  BMI: {progressStats.currentBMI}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Target size={24} color="#10b981" />
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {progressStats.progressPercentage}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Goal Progress
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Target BMI: {progressStats.targetBMI}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <TrendingUp size={24} color="#f59e0b" />
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {parseFloat(progressStats.actualVsExpected) > 0 ? '+' : ''}{progressStats.actualVsExpected}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  vs Expected (kg)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Calendar size={24} color="#8b5cf6" />
                <Typography variant="h4" color="secondary.main" fontWeight="bold">
                  {progressStats.daysRemaining}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Days Remaining
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="Weight Progress" />
            <Tab label="Body Measurements" />
            <Tab label="Activity Overview" />
            <Tab label="Goal Timeline" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
              <Typography variant="h6">
                Weight & BMI Progression
              </Typography>
              <ToggleButtonGroup
                value={chartView}
                exclusive
                onChange={(_, newView) => newView && setChartView(newView)}
                size="small"
              >
                <ToggleButton value="weight">
                  Weight Only
                </ToggleButton>
                <ToggleButton value="bmi">
                  BMI Only
                </ToggleButton>
                <ToggleButton value="both">
                  Both
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            {weightData.length === 0 ? (
              <Box display="flex" alignItems="center" justifyContent="center" height={isMobile ? 300 : 400}>
                <Typography color="text.secondary">No weight data recorded yet. Add your first weight entry to see your progress chart.</Typography>
              </Box>
            ) : (
            <Box height={isMobile ? 300 : 400}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weightData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#64748b"
                    fontSize={12}
                    interval="preserveEnd"
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  
                  {/* Weight Y-Axis */}
                  {chartView !== 'bmi' && (
                    <YAxis 
                      yAxisId="weight"
                      stroke="#6366f1"
                      fontSize={12}
                      domain={[
                        (database?.preferences?.goalWeight || 99.9) - 5,
                        (database?.preferences?.initialWeight || 144.2) + 10
                      ]}
                      label={{ 
                        value: 'Weight (kg)', 
                        angle: -90, 
                        position: 'insideLeft',
                        style: { fontSize: 12 }
                      }}
                    />
                  )}
                  
                  {/* BMI Y-Axis */}
                  {chartView !== 'weight' && (
                    <YAxis 
                      yAxisId="bmi" 
                      orientation={chartView === 'both' ? "right" : "left"}
                      stroke="#f59e0b"
                      fontSize={12}
                      domain={(() => {
                        const height = (database?.preferences?.height || 169) / 100; // in meters
                        const goalWeight = database?.preferences?.goalWeight || 99.9;
                        const initialWeight = database?.preferences?.initialWeight || 144.2;
                        const goalBMI = goalWeight / (height * height);
                        const initialBMI = initialWeight / (height * height);
                        return [
                          Math.max(18, Math.floor(goalBMI) - 2),
                          Math.min(50, Math.ceil(initialBMI) + 5)
                        ];
                      })()}
                      label={{ 
                        value: 'BMI', 
                        angle: chartView === 'both' ? 90 : -90, 
                        position: chartView === 'both' ? 'insideRight' : 'insideLeft',
                        style: { fontSize: 12 }
                      }}
                    />
                  )}
                  
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  
                  {/* Actual Weight Line */}
                  {chartView !== 'bmi' && (
                    <Line 
                      type="monotone" 
                      dataKey="actualWeight" 
                      stroke="#6366f1" 
                      strokeWidth={3}
                      yAxisId="weight"
                      dot={{ fill: '#6366f1', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#6366f1', strokeWidth: 2 }}
                      name="Actual Weight"
                      connectNulls
                    />
                  )}
                  
                  {/* Expected Weight Line (estimated trajectory) */}
                  {chartView !== 'bmi' && timeRange === 'goal' && (
                    <Line 
                      type="monotone" 
                      dataKey="expectedWeight" 
                      stroke="#9e9e9e" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      strokeOpacity={0.7}
                      yAxisId="weight"
                      dot={false}
                      name="Estimated Trajectory"
                    />
                  )}
                  
                  {/* Actual BMI Line */}
                  {chartView !== 'weight' && (
                    <Line 
                      type="monotone" 
                      dataKey="actualBMI" 
                      stroke="#f59e0b" 
                      strokeWidth={3}
                      yAxisId="bmi"
                      dot={{ fill: '#f59e0b', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#f59e0b', strokeWidth: 2 }}
                      name="Actual BMI"
                      connectNulls
                    />
                  )}
                  
                  {/* Expected BMI Line (estimated trajectory) */}
                  {chartView !== 'weight' && timeRange === 'goal' && (
                    <Line 
                      type="monotone" 
                      dataKey="expectedBMI" 
                      stroke="#9e9e9e" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      strokeOpacity={0.7}
                      yAxisId="bmi"
                      dot={false}
                      name="Estimated BMI Trajectory"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </Box>
            )}

            {timeRange === 'goal' && (
              <Box mt={2} p={2} bgcolor="#f8fafc" borderRadius={1}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Full Timeline:</strong> Journey from {goals && format(new Date(database?.preferences?.startDate || goals.startDate), 'MMM dd, yyyy')} ({goals?.initialWeight} kg) to target ({goals?.targetWeight} kg).
                  The dashed line shows the estimated linear trajectory from initial to target weight. Solid dots show your actual recorded measurements.
                </Typography>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom>
              Body Measurements Progress
            </Typography>
            {measurementsData.length > 0 ? (
              <Box height={400}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={measurementsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#64748b"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#64748b"
                      fontSize={12}
                      label={{ value: 'Measurements (cm)', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        color: '#0f172a'
                      }}
                    />
                    <Legend />
                    {['waist', 'chest', 'hips', 'thighs', 'arms', 'neck', 'shoulders'].map((key, index) => (
                      measurementsData.some(d => d[key as keyof typeof d]) && (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          stroke={COLORS[index]}
                          strokeWidth={2}
                          dot={{ fill: COLORS[index], strokeWidth: 2, r: 3 }}
                          activeDot={{ r: 5, stroke: COLORS[index], strokeWidth: 2 }}
                          name={key.charAt(0).toUpperCase() + key.slice(1)}
                        />
                      )
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="center" 
                height={400}
                bgcolor="#f8fafc"
                borderRadius={2}
                border="2px dashed #e2e8f0"
              >
                <Typography color="text.secondary" variant="h6">
                  No body measurements recorded yet.
                </Typography>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>
              Daily Activity Overview (Last 30 Days)
            </Typography>
            <Box height={400}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={activityData} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#64748b"
                    fontSize={12}
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    stroke="#64748b"
                    fontSize={12}
                    label={{ value: 'Count', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      border: '1px solid rgba(0,0,0,0.1)',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend />
                  <Bar 
                    dataKey="exercises" 
                    fill="#6366f1" 
                    name="Exercise (min)"
                    radius={[2, 2, 0, 0]}
                  />
                  <Bar 
                    dataKey="meals" 
                    fill="#10b981" 
                    name="Meals Completed"
                    radius={[2, 2, 0, 0]}
                  />
                  <Bar 
                    dataKey="supplements" 
                    fill="#f59e0b" 
                    name="Supplements Taken"
                    radius={[2, 2, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" gutterBottom>
              Goal Timeline Progress
            </Typography>
            {progressStats && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card sx={{ p: 3, mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Weight Progress
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                        {progressStats.weightLost} kg lost
                      </Typography>
                      <Box flex={1}>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min(100, parseFloat(progressStats.progressPercentage))}
                          sx={{ 
                            height: 12, 
                            borderRadius: 6,
                            backgroundColor: 'rgba(99, 102, 241, 0.1)',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 6,
                              background: 'linear-gradient(90deg, #6366f1, #818cf8)'
                            }
                          }}
                        />
                      </Box>
                      <Typography variant="body2" fontWeight="bold">
                        {progressStats.progressPercentage}%
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Target: {progressStats.totalToLose} kg total loss
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card sx={{ p: 3, mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Time Progress
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                        {progressStats.daysRemaining} days left
                      </Typography>
                      <Box flex={1}>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min(100, parseFloat(progressStats.timeProgressPercentage))}
                          sx={{ 
                            height: 12, 
                            borderRadius: 6,
                            backgroundColor: 'rgba(245, 158, 11, 0.1)',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 6,
                              background: 'linear-gradient(90deg, #f59e0b, #fbbf24)'
                            }
                          }}
                        />
                      </Box>
                      <Typography variant="body2" fontWeight="bold">
                        {progressStats.timeProgressPercentage}%
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Timeline elapsed since start date
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card sx={{ p: 3, height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Progress Insights
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      {parseFloat(progressStats.actualVsExpected) > 0 ? (
                        <Chip 
                          label={`Ahead by ${progressStats.actualVsExpected} kg`}
                          color="success" 
                          icon={<TrendingUp size={16} />}
                          sx={{ fontWeight: 'bold' }}
                        />
                      ) : parseFloat(progressStats.actualVsExpected) < -2 ? (
                        <Chip 
                          label={`Behind by ${Math.abs(parseFloat(progressStats.actualVsExpected))} kg`}
                          color="error"
                          icon={<TrendingDown size={16} />}
                          sx={{ fontWeight: 'bold' }}
                        />
                      ) : (
                        <Chip 
                          label="On Track" 
                          color="primary"
                          icon={<Target size={16} />}
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Compared to expected weekly progress of {goals?.weeklyTarget || 0} kg/week
                    </Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Card sx={{ p: 3, height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Remaining Goals
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                      <Scale size={20} color="#6366f1" />
                      <Typography variant="body1">
                        <strong>{progressStats.remainingWeight} kg</strong> to target weight
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Calendar size={20} color="#f59e0b" />
                      <Typography variant="body1">
                        <strong>{progressStats.daysRemaining}</strong> days until target date
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Target: {goals?.targetWeight} kg
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            )}
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};